import * as React from "react";
import {ChangeEvent, useState} from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import {UpdateSuccessSnackBar, UpdateErrorSnackBar} from "../../actions";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from "@material-ui/core";
import {
    AddAccountRequest,
    isErrorResponse,
    postAccount,
    getListsDetail,
    SNSDataList,
} from "../../util/api";
import {SetSNSLists} from "../../actions";
import {useSelector, useDispatch} from "react-redux";
import styles from "./css/addAccountModal.module.scss";

type AddAccountModalProps = {
    isOpen: boolean;
    setOpen: (open: boolean) => void;
    selectedList: number;
    isDemo?: boolean;
};

export const AddAccountModal: React.FC<AddAccountModalProps> = (
    props: AddAccountModalProps
) => {
    const {isOpen, setOpen, selectedList, isDemo} = props;

    const [accountURL, setAccountURL] = useState("");
    const [errorText, setErrorText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const snsLists = useSelector((state: any) => state.snsLists);
    const dispatch = useDispatch();

    const handleClose = () => {
        setOpen(false);
    };

    const handleAccountURLChange = (event: ChangeEvent<HTMLInputElement>) => {
        setAccountURL(event.target.value);
        setErrorText("");
    };

    const handleAddAccount = async () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        if (accountURL === "") {
            setErrorText("URLを入力してください");
            setIsLoading(false);
            return;
        }
        const request = parseAddAccountRequest(accountURL);
        if (request === null) {
            setErrorText("URLを正しく入力してください。");
            setIsLoading(false);
            return;
        }
        if (!isDemo) {
            const res = await postAccount(selectedList, request);
            if (isErrorResponse(res)) {
                dispatch(
                    UpdateErrorSnackBar("アカウントの追加に失敗しました。")
                );
                setIsLoading(false);
                return;
            }
        }

        dispatch(UpdateSuccessSnackBar("アカウントを追加しました"));
        setOpen(false);
        setAccountURL("");

        // リストを更新
        if (!isDemo) {
            await getListsDetail(selectedList)
                .then((listDetail) => {
                    if (isErrorResponse(listDetail)) {
                        dispatch(
                            UpdateErrorSnackBar("リストの取得に失敗しました。")
                        );
                        setIsLoading(false);
                        return;
                    }
                    const newSnsLists = snsLists.map((list: SNSDataList) => {
                        if (list.id === selectedList) {
                            listDetail.name = list.name;
                            return listDetail;
                        } else {
                            return list;
                        }
                    });
                    dispatch(SetSNSLists(newSnsLists));
                    setIsLoading(false);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const parseAddAccountRequest = (url: string): AddAccountRequest | null => {
        // twitter: https://twitter.com/{username}
        // youtube: https://www.youtube.com/channel/{userID}
        const urlArr = url.split("/");
        let serviceType = "";
        let accountId = "";
        switch (urlArr[2]) {
            case "www.youtube.com":
                serviceType = "youtube";
                accountId = urlArr[4];
                break;
            case "twitter.com":
                serviceType = "twitter";
                accountId = urlArr[3];
                break;
            default:
                serviceType = "other";
                break;
        }
        if (serviceType === "other" || !accountId) {
            return null;
        }
        return {
            serviceType: serviceType,
            accountId: accountId,
        };
    };

    return (
        <div>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
                classes={{paperScrollPaper: styles.dialog}}
            >
                <DialogTitle
                    className={styles.text}
                    id="form-dialog-title"
                    style={{textAlign: "center"}}
                >
                    アカウント追加
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        className={styles.text}
                        style={{
                            background: "#f2f2f2 0% 0%",
                            borderRadius: "13px",
                            padding: "16px",
                        }}
                    >
                        対応URLフォーマット
                        <br />
                        <div
                            style={{
                                fontSize: "14px",
                            }}
                        >
                            <div>
                                YouTube: https://www.youtube.com/channel/...
                            </div>
                            <div>Twitter: https://twitter.com/...</div>
                        </div>
                    </DialogContentText>
                    <TextField
                        autoFocus
                        id="listName"
                        label="チャンネル/アカウントURL"
                        variant="outlined"
                        value={accountURL}
                        onChange={handleAccountURLChange}
                        error={errorText !== ""}
                        helperText={errorText}
                        fullWidth
                        style={{background: "white", borderRadius: "13px"}}
                    />
                </DialogContent>
                <DialogActions className={styles.buttonWrapper}>
                    <Button onClick={handleClose} color="primary">
                        キャンセル
                    </Button>
                    <Button
                        onClick={handleAddAccount}
                        color="primary"
                        style={{
                            backgroundColor: "#0584d2",
                            color: "white",
                            fontWeight: "bold",
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "追加"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
